/**
 * Pure state machine for the V5 governed-case motion core.
 *
 * The sequence is a causal chain, not a loop: fragmented case matter aligns,
 * one governed rail forms, and the rail stops at an amber practitioner
 * threshold. Automatic progression can never pass that threshold — the
 * `released` phase is reachable only through an explicit RELEASE event, which
 * itself is honoured only once the threshold has been reached. Everything in
 * this module is pure and deterministic so the invariants can be proven
 * without timers or a DOM.
 */

export type GovernedCasePhase =
  'fragmented' | 'aligned' | 'rail' | 'held' | 'released';

/** Chronological phase order shared by the hook and the visual layers. */
export const GOVERNED_CASE_PHASE_ORDER: readonly GovernedCasePhase[] = [
  'fragmented',
  'aligned',
  'rail',
  'held',
  'released',
];

/**
 * Dwell times for the automatic phases, in milliseconds:
 * - `alignMs`  — how long the fragmented pose holds before alignment;
 * - `railMs`   — how long the aligned pose holds before the rail forms;
 * - `holdMs`   — how long the rail travels before the threshold hold engages.
 * The `held` phase has no duration: it lasts until the practitioner acts.
 */
export interface GovernedCaseTimings {
  alignMs: number;
  railMs: number;
  holdMs: number;
}

export const GOVERNED_CASE_DEFAULT_TIMINGS: GovernedCaseTimings = {
  alignMs: 1100,
  railMs: 1300,
  holdMs: 900,
};

export type GovernedCaseEvent = { type: 'AUTO_ADVANCE' } | { type: 'RELEASE' };

/** Static-hold mounts (reduced motion / save-data) begin at the threshold. */
export function initialGovernedCasePhase(
  staticHold: boolean,
): GovernedCasePhase {
  return staticHold ? 'held' : 'fragmented';
}

/**
 * The only transitions a timer may perform. `held` and `released` are fixed
 * points, so no amount of automatic advancement can cross the threshold.
 */
export function nextAutomaticPhase(
  phase: GovernedCasePhase,
): GovernedCasePhase {
  switch (phase) {
    case 'fragmented':
      return 'aligned';
    case 'aligned':
      return 'rail';
    case 'rail':
      return 'held';
    case 'held':
      return 'held';
    case 'released':
      return 'released';
  }
}

/**
 * RELEASE is honoured only at the threshold: before `held` the gate has not
 * been reached, so the event is ignored rather than skipping the causal
 * chain. Once released, the sequence stays released.
 */
export function governedCaseReducer(
  phase: GovernedCasePhase,
  event: GovernedCaseEvent,
): GovernedCasePhase {
  switch (event.type) {
    case 'AUTO_ADVANCE':
      return nextAutomaticPhase(phase);
    case 'RELEASE':
      return phase === 'held' || phase === 'released' ? 'released' : phase;
  }
}

/**
 * Delay before the next automatic transition, or `null` when the phase must
 * not progress on its own (`held` holds indefinitely; `released` is final).
 */
export function automaticDelayMs(
  phase: GovernedCasePhase,
  timings: GovernedCaseTimings,
): number | null {
  switch (phase) {
    case 'fragmented':
      return timings.alignMs;
    case 'aligned':
      return timings.railMs;
    case 'rail':
      return timings.holdMs;
    case 'held':
    case 'released':
      return null;
  }
}

function sanitizeDuration(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : fallback;
}

/** Merges partial overrides onto the defaults, rejecting non-finite values. */
export function resolveGovernedCaseTimings(
  overrides?: Partial<GovernedCaseTimings>,
): GovernedCaseTimings {
  return {
    alignMs: sanitizeDuration(
      overrides?.alignMs,
      GOVERNED_CASE_DEFAULT_TIMINGS.alignMs,
    ),
    railMs: sanitizeDuration(
      overrides?.railMs,
      GOVERNED_CASE_DEFAULT_TIMINGS.railMs,
    ),
    holdMs: sanitizeDuration(
      overrides?.holdMs,
      GOVERNED_CASE_DEFAULT_TIMINGS.holdMs,
    ),
  };
}
